// src/store/api/notificationStore.ts

import { getUserGymId } from "@/utils/gymAuth";
import { 
  createNotification, 
  createNotificationsBulk,
  createPaymentRemindersBulk,
} from "@/services/edgeFunctionWrappers/notificationsManagementWrapper";

export type NotificationType = "ADMIN_MESSAGE";
export type AdminMessagePayload = { text: string } & Record<string, any>;

export interface NotificationPlanInsert {
  user_id: string;                 // uuid (stringifié)
  type: NotificationType;          // 'ADMIN_MESSAGE'
  payload: AdminMessagePayload;    // jsonb
  scheduled_at: string;            // ISO string
  gym_id: string;                  // uuid gym
}

export class NotificationStore {
  /**
   * Crée une notification ADMIN_MESSAGE pour un utilisateur.
   */
  async createAdminMessage(
    userId: string | number,
    opts?: { text?: string; scheduledAt?: Date; extraPayload?: Record<string, any>; title?:string; }
  ): Promise<{ ok: boolean; error?: any }> {
    const payload: AdminMessagePayload = {
      text: opts?.text,
      title: opts?.title,
      ...(opts?.extraPayload ?? {}),
    };

    const { data, error } = await createNotification({
      targetUserId: String(userId),
      type: "ADMIN_MESSAGE",
      payload,
      scheduledAt: (opts?.scheduledAt ?? new Date()).toISOString(),
      gymId: getUserGymId(),
    });

    if (error) return { ok: false, error };
    return { ok: true };
  }

  /**
   * Crée des notifications ADMIN_MESSAGE pour une liste d'utilisateurs.
   * Chunk pour éviter les limites d'insert.
   */
  async createAdminMessages(
    userIds: Array<string | number>,
    opts?: { text?: string; scheduledAt?: Date; extraPayload?: Record<string, any>, title?:string }
  ): Promise<{ ok: boolean; inserted: number; error?: any }> {
    if (userIds.length === 0) return { ok: true, inserted: 0 };

    const payloadBase: AdminMessagePayload = {
      text: opts?.text,
      title: opts?.title,
      ...(opts?.extraPayload ?? {}),
    };

    const { data, error } = await createNotificationsBulk({
      userIds: userIds.map(id => String(id)),
      type: "ADMIN_MESSAGE",
      payload: payloadBase,
      scheduledAt: (opts?.scheduledAt ?? new Date()).toISOString(),
      gymId: getUserGymId(),
    });

    if (error) return { ok: false, inserted: 0, error };
    return { ok: true, inserted: data?.inserted || 0 };
  }

  async createPaymentReminders(
    userIds: string[],
    opts: { text: string; title: string }
  ): Promise<{ ok: boolean; inserted: number; ignored: number; error?: unknown }> {
    const uniqueUserIds = [...new Set(userIds)];
    if (uniqueUserIds.length === 0) return { ok: true, inserted: 0, ignored: 0 };

    const { data, error } = await createPaymentRemindersBulk({
      userIds: uniqueUserIds,
      title: opts.title,
      message: opts.text,
      gymId: getUserGymId(),
    });
    if (error || !data) return { ok: false, inserted: 0, ignored: 0, error };
    return { ok: true, inserted: data.inserted, ignored: data.ignored };
  }
}

// Singleton pratique
export const notificationStore = new NotificationStore();
